import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ArrowUpRight, Mail } from "lucide-react";
import { SITE, waLink } from "@/config/site";

const LINKS = [
  { label: "Garten", to: "/" },
  { label: "Leistungen", to: "/leistungen" },
  { label: "Projekte", to: "/projekte" },
  { label: "Über uns", to: "/ueber-uns" },
  { label: "Kontakt", to: "/kontakt" },
];

export const Footer = () => (
  <footer data-testid="site-footer" className="relative overflow-hidden bg-[#17251C] text-[#DCCDB4]">
    {/* organische Linie, zeichnet sich beim Scrollen */}
    <svg
      viewBox="0 0 1200 300"
      className="pointer-events-none absolute inset-x-0 top-0 h-52 w-full"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <motion.path
        d="M-20 210C160 120 300 250 470 170 640 90 760 200 920 120 1040 60 1140 110 1220 60"
        fill="none"
        stroke="#93A387"
        strokeWidth="1"
        strokeOpacity="0.55"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 2.6, ease: "easeInOut" }}
      />
      <motion.path
        d="M470 170c-18-30-6-62 26-74 4 40-6 60-26 74Z"
        fill="#93A387"
        fillOpacity="0.28"
        initial={{ opacity: 0, scale: 0.6 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, delay: 1.4 }}
      />
    </svg>

    <div className="relative mx-auto max-w-[1480px] px-5 pb-12 pt-40 md:px-10 md:pt-52">
      <div className="grid gap-14 lg:grid-cols-[1.35fr_1fr]">
        <div>
          <h2 className="display-xl text-[17vw] leading-[0.82] text-[#F3EDE2] sm:text-[12vw] lg:text-[8.5vw]">
            Hanse
            <br />
            Garten
          </h2>
          <p className="display-xl mt-3 text-[0.9rem] tracking-[0.55em] text-[#93A387]">Bremen</p>
        </div>

        <div className="grid gap-10 sm:grid-cols-2">
          <div>
            <p className="eyebrow !text-[#93A387]">Navigation</p>
            <ul className="mt-5 space-y-2.5">
              {LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-zäöü]+/g, "-")}`}
                    className="group inline-flex items-center gap-1.5 text-base text-[#DCCDB4] transition-colors duration-400 hover:text-[#F3EDE2]"
                  >
                    {l.label}
                    <ArrowUpRight
                      size={13}
                      className="opacity-0 transition-all duration-400 group-hover:translate-x-0.5 group-hover:opacity-100"
                    />
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="mt-7 space-y-2.5 border-t border-[#93A387]/20 pt-5">
              <li>
                <Link
                  to="/impressum"
                  data-testid="footer-link-impressum"
                  className="text-sm text-[#93A387] transition-colors hover:text-[#F3EDE2]"
                >
                  Impressum
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  data-testid="footer-link-datenschutz"
                  className="text-sm text-[#93A387] transition-colors hover:text-[#F3EDE2]"
                >
                  Datenschutz
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="eyebrow !text-[#93A387]">Adresse</p>
            <p className="mt-5 text-base leading-relaxed text-[#DCCDB4]">
              {SITE.name}
              <br />
              Inhaber: {SITE.owner}
              <br />
              Rechtsform: {SITE.legalForm}
              <br />
              {SITE.street}
              <br />
              {SITE.zipCity}
              <br />
              {SITE.country}
            </p>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-link"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#F3EDE2] transition-colors hover:text-[#B4623C]"
            >
              <MessageCircle size={15} /> WhatsApp {SITE.whatsappNumber}
            </a>
            <a
              href={`mailto:${SITE.email}`}
              data-testid="footer-email-link"
              className="mt-3 flex items-center gap-2 break-all text-sm font-semibold text-[#F3EDE2] transition-colors hover:text-[#B4623C]"
            >
              <Mail size={15} /> {SITE.email}
            </a>
          </div>
        </div>
      </div>

      <div className="mt-16 flex flex-col gap-3 border-t border-[#93A387]/20 pt-7 text-[0.7rem] uppercase tracking-[0.2em] text-[#93A387] md:flex-row md:items-center md:justify-between">
        <p>© 2026 {SITE.name}</p>
        <p>Garten- &amp; Landschaftsbau · Bremen &amp; Umgebung</p>
      </div>
    </div>
  </footer>
);
